import { useMemo } from 'react';

import type { APPLICANT_ATTRIBUTION_BOTH_TYPE } from 'cpool-constants';
import { APPLICANT_ATTRIBUTION_BOTH } from 'cpool-constants';
import type { Schema_Applicant } from 'cpool-schemas';
import { useTranslation } from 'react-i18next';

import LoansApplicantInformationTable from './LoansApplicantInformationTable';
import type { SchemaExistingLoans } from '~/features/leadDetails/schemas/existingLoans.schema';

const CLASS_TH = 'py-1.5 pr-2.5 text-10 text-center border-b-2 text-blue border-blue';
const CLASS_TD = 'py-1.5 pr-2.5 text-center text-10 text-grey whitespace-nowrap';

type GenericFormLoan = {
    type: keyof SchemaExistingLoans;
    id: string;

    beanspruchterBetrag?: number;
    zinssatz?: number;
    datumErsteZahlung?: Date;
    datumLetzteRate?: Date;
    rateMonatlich?: number;
    restschuld?: number;
    kreditinstitut?: string;
};

type CurrentLoansTableProps = {
    applicant?: Schema_Applicant;
    currentLoans: SchemaExistingLoans;
    owner: APPLICANT_ATTRIBUTION_BOTH_TYPE;
};

const FormLoansTable: React.FC<CurrentLoansTableProps> = ({ applicant, currentLoans, owner }) => {
    const { t } = useTranslation();

    const loans = useMemo(() => {
        const genericLoans: GenericFormLoan[] = [];

        Object.keys(currentLoans).forEach((loanType) => {
            currentLoans[loanType as keyof typeof currentLoans]?.forEach((loan) => {
                if (loan.gehoertZuAntragsteller === owner && loan.source === 'FORMULAR') {
                    // @ts-expect-error Ist schwer type safe zu machen
                    genericLoans.push({ ...loan, type: loanType });
                }
            });
        });

        return genericLoans;
    }, [currentLoans, owner]);

    if ((!loans.length && owner === APPLICANT_ATTRIBUTION_BOTH.BEIDE) || (!loans.length && !applicant)) return null;

    return (
        <div className="bg-grey-light px-4 py-2.5 mb-2.5">
            {applicant && <LoansApplicantInformationTable applicant={applicant} />}
            {loans.length > 0 && owner === APPLICANT_ATTRIBUTION_BOTH.BEIDE && (
                <div className="flex w-full justify-center items-center mb-2">
                    <span className="text-sm text-grey">{t('EXISTING_LOANS.BOTH_LOANS')}</span>
                </div>
            )}
            {loans.length > 0 && (
                <div className="max-w-full px-4 overflow-x-auto bg-white">
                    <table className="w-full table-auto">
                        <thead>
                            <tr>
                                <th className={CLASS_TH}>{t('EXISTING_LOANS.LOAN_TYPE')}</th>
                                <th className={CLASS_TH}>{t('EXISTING_LOANS.LOAN_SUM')}</th>
                                <th className={CLASS_TH}>{t('EXISTING_LOANS.LOAN_INTEREST')}</th>
                                <th className={CLASS_TH}>{t('EXISTING_LOANS.LOAN_START')}</th>
                                <th className={CLASS_TH}>{t('EXISTING_LOANS.LOAN_END')}</th>
                                <th className={CLASS_TH}>{t('EXISTING_LOANS.LOAN_RATE')}</th>
                                <th className={CLASS_TH}>{t('EXISTING_LOANS.LOAN_RESIDUAL_DEBT')}</th>
                                <th className={CLASS_TH}>{t('EXISTING_LOANS.LOAN_CREDIT_INSTITUTION')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loans.map((loan) => (
                                <tr key={loan.id}>
                                    <td className={CLASS_TD}>{t(`EXISTING_LOANS.TYPES.${loan.type}`)}</td>
                                    <td className={CLASS_TD}>
                                        {loan.beanspruchterBetrag ? `${loan.beanspruchterBetrag} €` : '-'}
                                    </td>
                                    <td className={CLASS_TD}>{loan.zinssatz ? `${loan.zinssatz} %` : '-'}</td>
                                    <td className={CLASS_TD}>
                                        {loan.datumErsteZahlung ? loan.datumErsteZahlung.toDateString() : '-'}
                                    </td>
                                    <td className={CLASS_TD}>
                                        {loan.datumLetzteRate ? loan.datumLetzteRate.toDateString() : '-'}
                                    </td>
                                    <td className={CLASS_TD}>{loan.rateMonatlich ? `${loan.rateMonatlich} €` : '-'}</td>
                                    <td className={CLASS_TD}>{loan.restschuld ? `${loan.restschuld} €` : '-'}</td>
                                    <td className={CLASS_TD}>{loan.kreditinstitut || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {!loans.length && (
                <div className="flex w-full justify-center items-center">
                    <span className="text-sm text-grey">
                        {t('EXISTING_LOANS.NO_LOANS', {
                            applicant: `${applicant?.personendaten.vorname} ${applicant?.personendaten.nachname}`,
                        })}
                    </span>
                </div>
            )}
        </div>
    );
};

export { FormLoansTable };
